import type { Labelled } from './common.js';
import { round2 } from './sales.js';

/**
 * باقات الخدمات.
 *
 * الباقة قالب تسعير: مجموعة خدمات تُباع معًا. إدراجها في عرض سعر ينسخ
 * بنودها إلى المستند، فيصبح المستند مستقلًا عن الباقة. هذا الفصل مقصود —
 * تعديل باقة اليوم لا يجوز أن يغيّر عرضًا وقّعه عميل الشهر الماضي.
 */

/* ==========================================================================
   طرق التسعير
   ========================================================================== */

export const PACKAGE_PRICING_MODES = ['SUM', 'DISCOUNT', 'FIXED'] as const;
export type PackagePricing = (typeof PACKAGE_PRICING_MODES)[number];

export interface PackagePricingDef extends Labelled<PackagePricing> {
  hint: string;
}

export const PACKAGE_PRICING_DEFS: readonly PackagePricingDef[] = [
  {
    key: 'SUM',
    label: 'مجموع الأسعار',
    kind: 'neutral',
    hint: 'سعر الباقة هو حاصل جمع أسعار خدماتها دون تعديل.',
  },
  {
    key: 'DISCOUNT',
    label: 'خصم نسبة',
    kind: 'info',
    hint: 'يُخصم من مجموع الأسعار نسبة مئوية ثابتة.',
  },
  {
    key: 'FIXED',
    label: 'سعر مقطوع',
    kind: 'success',
    hint: 'سعر واحد للباقة كلها بصرف النظر عن مجموع خدماتها.',
  },
] as const;

export const PACKAGE_PRICING_MAP = Object.fromEntries(
  PACKAGE_PRICING_DEFS.map((d) => [d.key, d]),
) as Readonly<Record<PackagePricing, PackagePricingDef>>;

export const PACKAGE_LAYOUTS = ['SUMMARY', 'DETAILED'] as const;
export type PackageLayout = (typeof PACKAGE_LAYOUTS)[number];

export interface PackageLayoutDef extends Labelled<PackageLayout> {
  hint: string;
}

export const PACKAGE_LAYOUT_DEFS: readonly PackageLayoutDef[] = [
  {
    key: 'DETAILED',
    label: 'مفصّل',
    kind: 'info',
    hint: 'بند مستقل لكل خدمة داخل الباقة، فيرى العميل ما يحصل عليه.',
  },
  {
    key: 'SUMMARY',
    label: 'مجمّع',
    kind: 'neutral',
    hint: 'بند واحد باسم الباقة وسعرها، والخدمات تُذكر في وصفه.',
  },
] as const;

export const PACKAGE_LAYOUT_MAP = Object.fromEntries(
  PACKAGE_LAYOUT_DEFS.map((d) => [d.key, d]),
) as Readonly<Record<PackageLayout, PackageLayoutDef>>;

/* ==========================================================================
   المدخلات
   ========================================================================== */

/** بند داخل الباقة بعد دمجه ببيانات الخدمة. */
export interface PackageItemView {
  serviceId: string;
  /** الوصف الظاهر: وصف البند إن وُجد، وإلا اسم الخدمة. */
  description: string;
  unit: string;
  quantity: number;
  /** السعر المعتمد: سعر البند داخل الباقة إن حُدِّد، وإلا سعر الخدمة. */
  unitPrice: number;
}

export interface PackagePricingInput {
  pricingMode: PackagePricing;
  fixedPrice?: number | null;
  discountPercent?: number | null;
}

/* ==========================================================================
   الحساب
   ========================================================================== */

/** مجموع أسعار الخدمات قبل تسعير الباقة. */
export function packageListTotal(items: readonly PackageItemView[]): number {
  return round2(items.reduce((sum, i) => sum + round2(i.quantity * i.unitPrice), 0));
}

export interface PackagePriceResult {
  /** مجموع الأسعار المفردة. */
  listTotal: number;
  /** ما يدفعه العميل فعلًا. */
  price: number;
  /** الفرق بين الاثنين. سالب إذا كانت الباقة أغلى من مجموع بنودها. */
  saving: number;
  /** نسبة التوفير من المجموع. */
  savingPercent: number;
}

/**
 * سعر الباقة النهائي.
 *
 * السعر المقطوع قد يفوق مجموع البنود — وهذا مشروع حين تشمل الباقة تنسيقًا
 * أو إدارة لا تُسعَّر كخدمة مستقلة. الدالة لا تمنعه، لكنها تُظهره كتوفير
 * سالب حتى لا يمر بلا انتباه.
 */
export function packagePrice(
  pkg: PackagePricingInput,
  items: readonly PackageItemView[],
): PackagePriceResult {
  const listTotal = packageListTotal(items);

  let price = listTotal;
  if (pkg.pricingMode === 'DISCOUNT') {
    const percent = clampPercent(pkg.discountPercent ?? 0);
    price = round2(listTotal * (1 - percent / 100));
  } else if (pkg.pricingMode === 'FIXED') {
    price = round2(Math.max(0, pkg.fixedPrice ?? 0));
  }

  const saving = round2(listTotal - price);
  const savingPercent = listTotal > 0 ? round2((saving / listTotal) * 100) : 0;
  return { listTotal, price, saving, savingPercent };
}

function clampPercent(v: number): number {
  if (!Number.isFinite(v)) return 0;
  return Math.min(100, Math.max(0, v));
}

/* ==========================================================================
   التوسيع إلى بنود مستند
   ========================================================================== */

/** بند جاهز للإدراج في عرض سعر أو فاتورة. */
export interface ExpandedLine {
  serviceId: string | null;
  description: string;
  unit: string;
  quantity: number;
  unitPrice: number;
  discount: number;
}

/**
 * توزيع سعر الباقة على بنودها.
 *
 * التوزيع بالتناسب مع قيمة كل بند، والباقي من التقريب يُحمَّل على البند
 * الأخير. بدون ذلك ينقص مجموع البنود عن سعر الباقة بقرش أو قرشين، فيظهر
 * المستند وكأنه لا يجمع صحيحًا — وهو أسرع ما يفقد العميل الثقة في فاتورة.
 */
function allocate(target: number, values: readonly number[]): number[] {
  const total = round2(values.reduce((s, v) => s + v, 0));
  const count = values.length;
  if (count === 0) return [];
  if (count === 1) return [round2(target)];

  const shares: number[] = [];
  let assigned = 0;

  for (let i = 0; i < count - 1; i++) {
    // عند انعدام المجموع يُقسَّم بالتساوي، وإلا لَقُسِمَ على صفر.
    const share = total > 0 ? round2((target * values[i]!) / total) : round2(target / count);
    shares.push(share);
    assigned = round2(assigned + share);
  }
  shares.push(round2(target - assigned));
  return shares;
}

/**
 * تحويل الباقة إلى بنود مستند.
 *
 * في الشكل المفصّل يظهر سعر الخدمة المفرد ويظهر نصيبها من خصم الباقة، فيرى
 * العميل قيمة ما يوفّره. في الشكل المجمّع يظهر بند واحد بسعر الباقة.
 */
export function expandPackage(
  pkg: PackagePricingInput & { name: string; description?: string | null },
  items: readonly PackageItemView[],
  layout: PackageLayout,
  options: { quantity?: number } = {},
): ExpandedLine[] {
  const multiplier = Math.max(1, options.quantity ?? 1);
  const { price } = packagePrice(pkg, items);

  if (items.length === 0) {
    return [
      {
        serviceId: null,
        description: pkg.name,
        unit: 'باقة',
        quantity: multiplier,
        unitPrice: price,
        discount: 0,
      },
    ];
  }

  if (layout === 'SUMMARY') {
    const contents = items
      .map((i) => (i.quantity === 1 ? i.description : `${i.description} (${i.quantity})`))
      .join('، ');
    return [
      {
        serviceId: null,
        description: `${pkg.name}\nتشمل: ${contents}`,
        unit: 'باقة',
        quantity: multiplier,
        unitPrice: price,
        discount: 0,
      },
    ];
  }

  const values = items.map((i) => round2(i.quantity * i.unitPrice));
  const targets = allocate(price, values);

  return items.map((item, index) => {
    const value = values[index]!;
    const target = targets[index]!;

    /*
     * الفرق يظهر كخصم على البند لا كتخفيض للسعر المفرد.
     *
     * إخفاؤه في سعر الوحدة يجعل الخدمة تبدو أرخص مما تُباع به منفردة، فيطالب
     * العميل بالسعر نفسه خارج الباقة. إظهاره كخصم يوضّح أنه مقابل الباقة.
     */
    if (target <= value) {
      return {
        serviceId: item.serviceId,
        description: item.description,
        unit: item.unit,
        quantity: round2(item.quantity * multiplier),
        unitPrice: item.unitPrice,
        discount: round2((value - target) * multiplier),
      };
    }

    // باقة أغلى من مجموع بنودها: يُرفع السعر بدل خصم سالب.
    const raised = item.quantity > 0 ? round2(target / item.quantity) : target;
    return {
      serviceId: item.serviceId,
      description: item.description,
      unit: item.unit,
      quantity: round2(item.quantity * multiplier),
      unitPrice: raised,
      discount: round2((round2(raised * item.quantity) - target) * multiplier),
    };
  });
}
